/**
 * Loading skeletons (design.md §12 / patterns.md §12).
 *
 * Use for the FIRST load only (no data yet, >200ms). For background refetches
 * of already-rendered data (keepPreviousData), keep the data on screen and show
 * a subtle, non-reflowing hint instead — do not swap in a skeleton.
 *
 * Geometry must match the final layout, so callers size the blocks.
 */

type SkeletonProps = {
  /** CSS width — use percentages or tokens, not raw px. */
  width?: string;
  /** CSS height — defaults to one text line (1em). */
  height?: string;
  className?: string;
};

export function Skeleton({ width, height, className }: SkeletonProps) {
  return (
    <span
      className={`wl-skeleton${className ? ` ${className}` : ""}`}
      style={{ width, height }}
      aria-hidden="true"
    />
  );
}

/**
 * Placeholder rows for a `.wl-table` body during first load. Renders one
 * shimmer bar per cell; widths vary per column for a natural look.
 */
export function SkeletonTableRows({
  rows = 8,
  columns,
  widths,
}: {
  rows?: number;
  columns: number;
  /** Optional per-column width pattern, repeated across columns. */
  widths?: string[];
}) {
  const pattern = widths ?? ["70%", "55%", "60%", "50%", "65%", "45%"];
  return (
    <>
      {Array.from({ length: rows }, (_, r) => (
        <tr key={r} aria-hidden="true">
          {Array.from({ length: columns }, (_, c) => (
            <td key={c}>
              <Skeleton width={pattern[c % pattern.length]} height="var(--space-3)" />
            </td>
          ))}
        </tr>
      ))}
    </>
  );
}
